// The invoice table tag's options, read into the Define Invoice Parameters dialog and written back from it, so an
// existing {{Patient.SelectedInvoice.WithPredefinedParameters(…)}} can be opened, changed and saved in place.
//
// Kept out of dialogs/utils: that file is imported by the form converter (invoice-table.util), which runs wherever
// a Form renders and must not load the invoice table's column code with it.
import { getConfigurableLineItemsHeaders } from "../merge-tags/merge-tags-values/common/configurable-merge-tags";
import {
  STANDARD_TOTALS_FONT_SIZE_VALUE,
  getMaxExplicitTableFontSizePx,
  parseTableFontSizePx
} from "../merge-tags/merge-tags-values/common/table-font-size.utils";
import { createDefaultValues, filterConfigurationSections } from "./utils";
// import type, as dialogs/utils does: a value import would load the dialog (and the store) here.
import type { SelectSaleConfigurationValues } from "./word-processor-select-sale-configuration-dialog.component";

type TagOptions = Record<string, unknown>;

/** An invoice table tag the dialog is inserting, or editing in place, and the options it opens with. */
export type InvoiceTableTagRequest = {
  mode: "insert" | "edit";
  options: Record<string, any>;
};
type FormKey = keyof SelectSaleConfigurationValues;

const FONT_SIZE_KEYS = new Set<string>(["font_size", "totals_font_size"]);

/** The totals rows: the Totals section's columns, which share no prefix. */
const TOTALS_KEYS = new Set<string>([
  "total_show_services_dispensing_fees",
  "total_show_discounts",
  "total_tax",
  "total_amount",
  "total_patient_payments",
  "total_insurance_payments",
  "amount_due_from_insurance",
  "amount_due_from_patient",
  "balance_due",
  "total_show_cost_of_ha"
]);

/** Each section's switch, and which options are its columns. */
const SECTIONS: Array<{ include: FormKey; holds: (key: string) => boolean }> = [
  ["include_hearing_aids", "hearing_aids_"],
  ["include_domes", "domes_"],
  ["include_receivers", "receivers_"],
  ["include_tubes", "tubes_"],
  ["include_earmolds", "earmolds_"],
  ["include_batteries", "batteries_"],
  ["include_chargers", "chargers_"],
  ["include_remotes", "remotes_"],
  ["include_others", "others_"],
  ["include_services_dispensing_fees", "services_"]
]
  .map(([include, prefix]) => ({
    include: include as FormKey,
    holds: (key: string) => key.startsWith(prefix)
  }))
  .concat({
    include: "include_totals",
    holds: (key: string) => TOTALS_KEYS.has(key)
  });

const blankValues = () =>
  createDefaultValues({} as SelectSaleConfigurationValues);

/** Every option the dialog knows, so options it doesn't (a newer or converted tag's) can be kept. Built on first
 * use, not at import: this module is loaded by the editor everywhere, and tests mock what the dialog's defaults read. */
let knownKeys: Set<string> | undefined;
const getKnownKeys = () => (knownKeys ??= new Set(Object.keys(blankValues())));

/** An option's value as the tag prints it: "yes", a size, or null when it is off. */
const spelled = (value: unknown): string | null => {
  if (value === true || value === "yes" || value === "true") return "yes";
  if (
    value === false ||
    value === "no" ||
    value === "false" ||
    value === null ||
    value === undefined ||
    value === ""
  )
    return null;
  return String(value);
};

const isOn = (value: unknown) => spelled(value) !== null;

/**
 * The dialog's values for a tag's options.
 *
 * A tag stores only the options that are on, but the dialog reads a missing option as on (it is how a new table
 * starts with every column), so passing a tag straight to `createDefaultValues` would switch back on every column
 * the tag had turned off, and Apply would add them to the table. Here a missing option is off, except in a section
 * the tag leaves out entirely (no switch and no columns): its columns were never chosen, so they start as a new
 * table's do, ready for when that section is switched on.
 */
export const formValuesFromTagOptions = (
  options: TagOptions
): SelectSaleConfigurationValues => {
  const blank = blankValues();
  // Nothing chosen yet: a new table, which starts as the dialog always has.
  if (!Object.values(options).some(isOn)) return blank;
  const sectionOf = (key: string) =>
    SECTIONS.find((section) => section.holds(key));
  const isUnchosen = (section: (typeof SECTIONS)[number]) =>
    !isOn(options[section.include]) &&
    !Object.keys(options).some(
      (key) => section.holds(key) && isOn(options[key])
    );

  const data = Object.fromEntries(
    (Object.keys(blank) as FormKey[]).map((key) => {
      if (FONT_SIZE_KEYS.has(key)) return [key, options[key]];
      const section = sectionOf(key);
      if (section && section.include !== key && isUnchosen(section)) {
        return [key, blank[key]];
      }
      return [key, isOn(options[key])];
    })
  ) as unknown as SelectSaleConfigurationValues;

  return createDefaultValues(data);
};

/**
 * The tag's options for the dialog's values: what Apply saves. Moved here from the dialog's own save so the
 * insert, the edit and the stored configuration all write the same thing.
 */
export const tagOptionsFromFormValues = (
  data: SelectSaleConfigurationValues,
  { fontSizeEnabled }: { fontSizeEnabled: boolean }
): Record<string, any> => {
  const result: Record<string, any> = filterConfigurationSections(data);

  // Auto is expressed by omitting the option entirely, so existing templates (and any token without font_size)
  // keep today's output. The picker + snap effect already keep the value within the ceiling for the persisted
  // column count; the clamp here is the final guarantee that what we persist is exactly what renders. With the
  // feature flag off the picker is hidden, so nothing is persisted: a re-saved token drops any previous choice and
  // returns to auto.
  const explicitFontSizePx = parseTableFontSizePx(data.font_size);
  if (fontSizeEnabled && explicitFontSizePx !== undefined) {
    result.font_size = String(
      Math.min(
        explicitFontSizePx,
        getMaxExplicitTableFontSizePx(
          getConfigurableLineItemsHeaders(result).length
        )
      )
    );
  }

  // ENG-2462: Standard is saved by leaving the option out, so a template saved before the choice existed prints
  // exactly as it did.
  if (
    fontSizeEnabled &&
    result.include_totals &&
    data.totals_font_size !== STANDARD_TOTALS_FONT_SIZE_VALUE
  ) {
    result.totals_font_size = data.totals_font_size;
  }

  return result;
};

/** The options of `original` the dialog doesn't know, added back so an edit never drops them. */
export const withUnknownOptions = (
  options: Record<string, any>,
  original: TagOptions
): Record<string, any> => {
  const known = getKnownKeys();
  const unknown = Object.entries(original).filter(
    ([key]) => !known.has(key) && !(key in options)
  );
  return unknown.length
    ? { ...options, ...Object.fromEntries(unknown) }
    : options;
};

/** The options that print, as one comparable string: on/off spellings and key order don't count. */
const printedOptions = (options: TagOptions) =>
  Object.entries(options)
    .filter(([, value]) => isOn(value))
    .map(([key, value]) => `${key}=${spelled(value)}`)
    .sort()
    .join(",");

/** True when two option sets print the same table, so an unchanged edit can leave the tag's text as it was. */
export const sameTagOptions = (a: TagOptions, b: TagOptions) =>
  printedOptions(a) === printedOptions(b);
